'use client';

import { c as _c } from "react/compiler-runtime";
import { Call, CallSolid, NavArrowLeft, NavArrowRight } from '@byway/icons';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useLocale, useTranslations } from 'next-intl';
import React, { type MouseEventHandler, useEffect, useState } from 'react';
import { Button, DialogTrigger } from 'react-aria-components';
import { Image } from 'react-datocms';
import { Text } from 'ui';
import { Stack } from '~/components/Layout/Stack';
import { Link } from '~/components/Link';
import { Popover } from '~/components/Popover';
import { buildMapEntryHref } from '~/constants';
import useBreakpoint from '~/hooks/useBreakpoint';
import { useScrollDirection } from '~/hooks/useScrollDirection';
import { usePartner } from '~/libs/partners';
import { isDynamicBlogpostsMarker } from '~/transformers/Dato/NavigationTransformer';
import type { MenuItem } from '~/types/Menu.types';
import { gtag, trackEvent } from '~/utils';
import { getCookie, setCookie } from '~/utils/cookies';
import { useSharedRouter } from '~/utils/nextMigration/router';
import { CURRENT_PROMO } from '~/utils/promoHelper';
import { isTripV2Enabled } from '~/utils/tripV2Flag';
import { useSurvey } from '../../../Survey/SurveyProvider';
const BuildTripButton = dynamic(() => import('../../../Playground/BuildTripButton/BuildTripButton').then(mod => mod.BuildTripButton), {
  ssr: false
});
import NavigationLogo from '../../NavigationLogo';
import { NavSearch } from './components/NavSearch/NavSearch';
import { PreferencesDialog } from './components/PreferencesDialog/PreferencesDialog';
import styles from './Navigation.module.css';
import type { MobileMenuProps, NavDropdownItemsProps, NavDropdownListDetailProps, NavDropdownTilesProps, NavigationProps } from './Navigation.types';
import { toNavHref } from './toNavHref';
const showContactOptions = process.env.NEXT_PUBLIC_SHOW_CONTACT_OPTIONS === 'true';
const MobileMenu = t0 => {
  const $ = _c(34);
  const {
    navigationOpen,
    toggleNavigation,
    isMobileContactPopoverOpen: t1,
    setIsMobileContactPopoverOpen
  } = t0;
  const isMobileContactPopoverOpen = t1 === undefined ? false : t1;
  const t = useTranslations();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PreferencesDialog />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== isMobileContactPopoverOpen || $[2] !== setIsMobileContactPopoverOpen || $[3] !== t) {
    t3 = showContactOptions && setIsMobileContactPopoverOpen && <DialogTrigger isOpen={isMobileContactPopoverOpen} onOpenChange={setIsMobileContactPopoverOpen}><Button aria-label={t("aria.call")} className={styles.contactButton} onClick={_temp}><Call decorative={true} size="large" /></Button><Popover placement="bottom" maxWidth="300px" close={true} ariaLabel={t("aria.contact_phone")} onOpenChange={setIsMobileContactPopoverOpen} border={false}><Stack space="--by-space-200"><div className={styles.contactPopoverContent}><div className={styles.contactPopoverIcon}><CallSolid size="scale" decorative={true} /></div><div className={styles.contactPopoverText}><p className={styles.contactPopoverTitle}>{t("common.phone")}</p><div className={styles.contactPopoverDescription}><div className={styles.contactPopoverPhone}><Link href={`tel:${t("common.phone_1_tel")}`}>{t("common.phone_1_display")}</Link></div>{t("common.phone_2_display") && <div className={styles.contactPopoverPhone}><Link href={`tel:${t("common.phone_2_tel")}`}>{t("common.phone_2_display")}</Link></div>}</div></div></div></Stack></Popover></DialogTrigger>;
    $[1] = isMobileContactPopoverOpen;
    $[2] = setIsMobileContactPopoverOpen;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <NavSearch />;
    t5 = <BuildTripButton />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== navigationOpen || $[8] !== t) {
    t6 = navigationOpen ? t("aria.close_menu") : t("aria.open_menu");
    $[7] = navigationOpen;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== navigationOpen) {
    t7 = clsx(styles.hamburgerBar, {
      [styles.hamburgerBarOpen]: navigationOpen
    });
    $[10] = navigationOpen;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <span className={t7} />;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== navigationOpen) {
    t9 = clsx(styles.hamburgerBar, {
      [styles.hamburgerBarHidden]: navigationOpen
    });
    $[14] = navigationOpen;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <span className={t9} />;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== navigationOpen) {
    t11 = clsx(styles.hamburgerBar, {
      [styles.hamburgerBarOpen]: navigationOpen
    });
    $[18] = navigationOpen;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <span className={t11} />;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12 || $[24] !== t8) {
    t13 = <div className={styles.hamburgerIcon}>{t8}{t10}{t12}</div>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t8;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== navigationOpen || $[27] !== t13 || $[28] !== t6 || $[29] !== toggleNavigation) {
    t14 = <button type="button" className={styles.hamburgerButton} onClick={toggleNavigation} aria-expanded={navigationOpen} aria-controls="main-nav" aria-label={t6}>{t13}</button>;
    $[26] = navigationOpen;
    $[27] = t13;
    $[28] = t6;
    $[29] = toggleNavigation;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t3) {
    t15 = <div className={styles.mobileControls}>{t2}{t3}{t4}{t5}{t14}</div>;
    $[31] = t14;
    $[32] = t3;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
};

// Make MobileMenu a memoized component to prevent re-renders when props don't change
const MemoizedMobileMenu = React.memo(MobileMenu);

// Flat dropdown links (groups are already flattened by `flatItems`). Image tiles are rendered
// separately by NavDropdownTiles. Links stay raw <a> here; the ~/components/Link (soft-nav)
// migration is owned by the disclosure-nav a11y rebuild.
const NavDropdownItems = t0 => {
  const $ = _c(9);
  const {
    items,
    surveyHandler
  } = t0;
  const locale = useLocale();
  if (!items?.length) {
    return null;
  }
  let t1;
  if ($[0] !== items || $[1] !== locale || $[2] !== surveyHandler) {
    let t2;
    if ($[4] !== locale || $[5] !== surveyHandler) {
      t2 = item => {
        if (Array.isArray(item.items)) {
          return null;
        }
        return <li key={item.id} className={styles.dropdownListItem}><a onClick={surveyHandler} href={toNavHref(item, locale)} className={styles.dropdownLink}><Text variant="body-1" as="span" className={styles.dropdownLinkText}>{item.title}</Text></a></li>;
      };
      $[4] = locale;
      $[5] = surveyHandler;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = items.map(t2);
    $[0] = items;
    $[1] = locale;
    $[2] = surveyHandler;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <ul className={styles.dropdownList}>{t1}</ul>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

// A group carries an `items` array (a leaf never does); empty groups are treated as absent.
const isNonEmptyGroup = (item: MenuItem): boolean => Array.isArray(item.items) && item.items.length > 0;

// Journal (latest blogposts) is on hold until it has a design; the data still arrives, it just isn't rendered.
const visibleItems = (menu: MenuItem): MenuItem[] => (menu.items ?? []).filter(item => !isDynamicBlogpostsMarker(item));
const visibleGroups = (menu: MenuItem): MenuItem[] => visibleItems(menu).filter(isNonEmptyGroup);

// One group has nothing to choose between, so it renders flat (e.g. Inspiration → Collections).
const isListDetail = (menu: MenuItem): boolean => visibleGroups(menu).length > 1;
const layoutOf = (menu: MenuItem) => isListDetail(menu) ? 'list-detail' : 'flat';
// Tiles over plain links (Design a trip) replace those links once tiles show; a flattened group
// (Inspiration → Collections) keeps its links beside them.
const isTilesOnly = (menu: MenuItem): boolean => Boolean(menu.tiles?.length) && !visibleItems(menu).some(isNonEmptyGroup);
// list-detail and tiled-with-links dropdowns are wide; the rest (About, Design a trip) size to content
const isWide = (menu: MenuItem): boolean => isListDetail(menu) || Boolean(menu.tiles?.length) && !isTilesOnly(menu);
const flatItems = (menu: MenuItem): MenuItem[] => visibleItems(menu).flatMap(item => isNonEmptyGroup(item) ? item.items ?? [] : [item]);

// Sections containing groups: group titles on the left select which group's links show in the
// middle (desktop), or expand in place as an accordion (mobile). Chosen by structure, not section
// order/title, so the content team can reorder or rename sections.
const NavDropdownListDetail = t0 => {
  const $ = _c(42);
  const {
    groups,
    surveyHandler,
    navigationOpen,
    openDropdown
  } = t0;
  const locale = useLocale();
  const [selectedId, setSelectedId] = useState(groups[0]?.id);
  const [expandedId, setExpandedId] = useState(null);
  let t1;
  if ($[0] !== groups || $[1] !== selectedId) {
    t1 = groups.find(group => group.id === selectedId) ?? groups[0];
    $[0] = groups;
    $[1] = selectedId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  let t2;
  if ($[3] !== expandedId || $[4] !== navigationOpen || $[5] !== openDropdown) {
    t2 = group_0 => {
      setSelectedId(group_0.id);
      if (navigationOpen) {
        openDropdown();
      }
      setExpandedId(expandedId === group_0.id ? null : group_0.id);
    };
    $[3] = expandedId;
    $[4] = navigationOpen;
    $[5] = openDropdown;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const selectGroup = t2;
  let t3;
  if ($[7] !== expandedId || $[8] !== selectGroup) {
    t3 = (e, group_1) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectGroup(group_1);
      } else {
        if (e.key === "ArrowDown" && expandedId === group_1.id) {
          e.preventDefault();
          const firstSubItem = document.querySelector(`[data-parent-id="${group_1.id}"] a`);
          (firstSubItem as HTMLElement)?.focus();
        } else {
          if (e.key === "Escape") {
            setExpandedId(null);
          }
        }
      }
    };
    $[7] = expandedId;
    $[8] = selectGroup;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleKeyDown = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (e_0, parentId) => {
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        setExpandedId(null);
        const parentButton = document.querySelector(`[data-item-id="${parentId}"]`);
        (parentButton as HTMLElement)?.focus();
      }
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleSubItemKeyDown = t4;
  let t5;
  if ($[11] !== expandedId || $[12] !== groups || $[13] !== handleKeyDown || $[14] !== locale || $[15] !== selectGroup || $[16] !== selected?.id || $[17] !== surveyHandler) {
    let t6;
    if ($[19] !== expandedId || $[20] !== handleKeyDown || $[21] !== locale || $[22] !== selectGroup || $[23] !== selected?.id || $[24] !== surveyHandler) {
      t6 = group_2 => <li key={group_2.id} className={styles.dropdownListItem} role="none"><button type="button" role="menuitem" aria-haspopup="true" aria-expanded={expandedId === group_2.id} data-item-id={group_2.id} onClick={e_1 => {
          e_1.preventDefault();
          selectGroup(group_2);
        }} onKeyDown={e_2 => handleKeyDown(e_2, group_2)} className={clsx(styles.dropdownLink, {
          [styles.dropdownLinkActive]: selected?.id === group_2.id,
          [styles.dropdownLinkActiveMobile]: expandedId === group_2.id
        })}><h3 className={clsx(styles.dropdownLinkText, {
            [styles.dropdownLinkTextExpanded]: expandedId === group_2.id
          })}>{group_2.title}</h3><span className={clsx(styles.dropdownChevron, {
            [styles.dropdownChevronExpanded]: expandedId === group_2.id
          })}><NavArrowRight decorative={true} size="large" /></span></button>{expandedId === group_2.id && <ul className={styles.mobileDropdownContent} data-parent-id={group_2.id}>{group_2.items?.map(subItem => <li key={subItem.id} className={styles.mobileDropdownItemWrapper} role="none"><a role="menuitem" onClick={surveyHandler} onKeyDown={e_3 => handleSubItemKeyDown(e_3, group_2.id)} href={toNavHref(subItem, locale)} className={styles.mobileDropdownItem}><Text variant="body-1" as="span">{subItem.title}</Text></a></li>)}</ul>}</li>;
      $[19] = expandedId;
      $[20] = handleKeyDown;
      $[21] = locale;
      $[22] = selectGroup;
      $[23] = selected?.id;
      $[24] = surveyHandler;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    t5 = groups.map(t6);
    $[11] = expandedId;
    $[12] = groups;
    $[13] = handleKeyDown;
    $[14] = locale;
    $[15] = selectGroup;
    $[16] = selected?.id;
    $[17] = surveyHandler;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[26] !== t5) {
    t6 = <div className={styles.regionsNav}><ul className={styles.dropdownList}>{t5}</ul></div>;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const t7 = selected?.title;
  let t8;
  if ($[28] !== t7) {
    t8 = <p className={styles.middleCountriesTitle}>{t7}</p>;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== locale || $[31] !== selected?.items || $[32] !== surveyHandler) {
    t9 = selected?.items?.map(subItem_0 => <li key={subItem_0.id} className={styles.dropdownCountry} role="none"><a role="menuitem" onClick={surveyHandler} href={toNavHref(subItem_0, locale)} className={styles.dropdownCountryItem}><Text variant="body-1" as="span">{subItem_0.title}</Text></a></li>);
    $[30] = locale;
    $[31] = selected?.items;
    $[32] = surveyHandler;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t9) {
    t10 = <ul className={styles.middleCountriesList}>{t9}</ul>;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t8) {
    t11 = <div className={styles.middleCountries} data-test-id="nav-country-links">{t8}{t10}</div>;
    $[36] = t10;
    $[37] = t8;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t11 || $[40] !== t6) {
    t12 = <>{t6}{t11}</>;
    $[39] = t11;
    $[40] = t6;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
};

// Desktop-only image tiles for a section's dropdown (MegaNav `tiledLinks`). Renders beside the link
// list; the `.dropdownTiles` pane is hidden on mobile via CSS. Raw <a> like the list — the
// ~/components/Link soft-nav migration is Card 4's job.
const NavDropdownTiles = t0 => {
  const $ = _c(9);
  const {
    tiles,
    surveyHandler
  } = t0;
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== surveyHandler || $[2] !== tiles) {
    let t2;
    if ($[4] !== locale || $[5] !== surveyHandler) {
      t2 = tile => {
        const image = tile.backgroundImage?.responsiveImage;
        return <div key={tile.id} className={styles.dropdownTile}><a onClick={surveyHandler} href={toNavHref(tile, locale)} className={styles.dropdownTileImageWrapper}>{image && <Image data={image} layout="fill" objectFit="cover" sizes="auto, 300px" imgClassName={styles.dropdownTileImage} />}<div className={styles.dropdownTileContent}><p className={styles.dropdownTileTitle}>{tile.title}</p>{tile.excerpt && <p>{tile.excerpt}</p>}</div></a></div>;
      };
      $[4] = locale;
      $[5] = surveyHandler;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = tiles.map(t2);
    $[0] = locale;
    $[1] = surveyHandler;
    $[2] = tiles;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className={styles.dropdownTiles}>{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
function Navigation({
  isSmall = false,
  nav
}: Readonly<NavigationProps>) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useSharedRouter();
  const partner = usePartner();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [navigationDropdownIndex, setNavigationDropdownIndex] = useState<number | null>(null);
  const {
    setIsOpen: setIsGeneralSurveyOpen
  } = useSurvey('general') ?? {};
  const {
    isPromoLive
  } = CURRENT_PROMO();
  const {
    scrollDirection,
    isVisible
  } = useScrollDirection(); // Remove the 80 argument
  const {
    isMobile
  } = useBreakpoint();
  const [isMobileInitialized, setIsMobileInitialized] = useState(false);
  const [isMobileContactPopoverOpen, setIsMobileContactPopoverOpen] = useState(false);
  const [isDesktopContactPopoverOpen, setIsDesktopContactPopoverOpen] = useState(false);

  // Check if we're on a journey page or trip overview page (these have their own sticky nav context)
  const isJourneyPage = router.pathname?.includes('/journey/');
  const isTripOverviewPage = isTripV2Enabled() && isJourneyPage;

  // Determine if navigation should be sticky (desktop only, not on journey pages, and only when scrolling up)
  const shouldBeSticky = !isMobile && !isJourneyPage && isVisible && scrollDirection === 'up';
  const isOnCheckoutDetailsPage = router.pathname?.includes('checkout/details');
  const hideCTABlock = router.pathname?.includes('map');
  const surveyHandler: MouseEventHandler = e => {
    if (isOnCheckoutDetailsPage) {
      if (getCookie('hasHadGeneralSurvey')) return;
      e.preventDefault();
      setIsGeneralSurveyOpen(true);
      gtag({
        event: 'general_survey_triggered'
      });
      setCookie('hasHadGeneralSurvey', true);
    }
  };
  useEffect(() => {
    if (!isMobile) {
      setNavigationOpen(false);
    }
    setIsMobileInitialized(true);
  }, [isMobile]);

  // Close navigation on route change
  useEffect(() => {
    if (isMobile && navigationOpen) {
      setNavigationOpen(false);
      setNavigationDropdownIndex(null);
    }
  }, []);
  const toggleNavigation = () => {
    setNavigationDropdownIndex(null);
    setNavigationOpen(!navigationOpen);
  };

  // Add state to track focused menu item
  const [focusedMenuIndex, setFocusedMenuIndex] = useState<number | null>(null);

  // Update keyboard handlers
  const handleKeyDown = (e_0: React.KeyboardEvent, index: number, menu: any) => {
    if (e_0.key === 'Enter' || e_0.key === ' ') {
      e_0.preventDefault();
      // A leaf (no nested items) navigates; a group opens its dropdown.
      if (isMobile && menu.url && !menu.items?.length) {
        globalThis.location.href = toNavHref(menu, locale);
        return;
      }
      setNavigationDropdownIndex(index);
      setFocusedMenuIndex(index);
    } else if (e_0.key === 'Escape') {
      setNavigationDropdownIndex(null);
      setFocusedMenuIndex(null);
    } else if (e_0.key === 'ArrowDown' && navigationDropdownIndex === index) {
      // Focus first dropdown item
      e_0.preventDefault();
      const dropdown = document.querySelector(`[data-dropdown-index="${index}"] a`);
      (dropdown as HTMLElement)?.focus();
    }
  };

  // Add focus handlers
  const handleFocus = (index_0: number) => {
    setFocusedMenuIndex(index_0);
    if (!isMobile) {
      setNavigationDropdownIndex(index_0);
    }
  };
  const handleBlur = (e_1: React.FocusEvent) => {
    // Check if the next focus target is still within the navigation
    const isStillInNav = e_1.currentTarget.contains(e_1.relatedTarget as Node);
    if (!isStillInNav) {
      setFocusedMenuIndex(null);
      setNavigationDropdownIndex(null);
    }
  };
  return <header className={clsx(styles.header, {
    [styles.partner]: partner !== undefined,
    [styles.sticky]: shouldBeSticky
  })} style={shouldBeSticky ? {
    top: '0px'
  } : undefined}>
      <a href="/" className="sr-only focus:not-sr-only focus:absolute focus:bg-white">
        {t('navigation.skip_to_content')}
      </a>
      <div className={clsx(styles.container, isSmall ? styles.containerSmall : styles.containerRegular, {
      [styles.containerTripOverview]: isTripOverviewPage
    })}>
        <div className={styles.leftSection}>
          <div onClick={surveyHandler} className={clsx(styles.logoWrapper, {
          [styles.logoWrapperRegular]: !isSmall,
          [styles.logoWrapperSmall]: isSmall
        })}>
            <NavigationLogo theme="light" />
          </div>

          {/* MOBILE MENU WHEN NAVIGATION IS CLOSED */}
          <MemoizedMobileMenu navigationOpen={navigationOpen} toggleNavigation={toggleNavigation} isMobileContactPopoverOpen={isMobileContactPopoverOpen} setIsMobileContactPopoverOpen={setIsMobileContactPopoverOpen} />

          {/* MAIN NAVIGATION */}
          <nav id="main-nav" className={clsx(styles.mainNav, {
          [styles.mainNavOpen]: navigationOpen,
          [styles.promotion]: isPromoLive && !partner,
          [styles.partner]: partner && isPromoLive
        })} onBlur={handleBlur}>
            {/* MOBILE MENU WHEN NAVIGATION IS OPEN TO FIX IPHONE SCROLL BUG */}
            <div className={styles.mobileNavHeader}>
              <div className={clsx(styles.logoWrapper, {
              [styles.logoWrapperRegular]: !isSmall,
              [styles.logoWrapperSmall]: isSmall
            })}>
                <NavigationLogo theme="light" />
              </div>
              <MemoizedMobileMenu navigationOpen={navigationOpen} toggleNavigation={toggleNavigation} />
            </div>

            <ul className={styles.navList}>
              {/* Mobile-only Build Trip button at the top - only show after mobile state is initialized */}
              {isMobileInitialized && isMobile && !hideCTABlock && <li className={styles.navItem} role="none">
                  <a href={toNavHref({
                url: buildMapEntryHref(locale)
              }, locale)} role="menuitem" className={clsx(styles.navLink, styles.navLinkNoChildren)} data-ua-click="playground-trip-cta">
                    <Text variant="body-1" as="span" className={styles.navLinkText}>
                      {t('navigation.trip_build')}
                    </Text>
                  </a>
                </li>}
              {nav?.map((menu_0, index_1) => <li className={styles.navItem} key={index_1} role="none">
                  <a href={toNavHref(menu_0, locale)} role="menuitem" aria-expanded={navigationDropdownIndex === index_1} className={clsx(styles.navLink, {
                [styles.navLinkNoChildren]: !menu_0.items,
                [styles.navLinkActive]: focusedMenuIndex === index_1
              })} onClick={e_2 => {
                e_2.preventDefault(); // Prevent default link behavior
                if (navigationOpen && menu_0.items) {
                  setNavigationDropdownIndex(index_1);
                  setFocusedMenuIndex(index_1);
                } else if (menu_0.url && !menu_0.items?.length) {
                  // A leaf navigates; a group only toggles its dropdown.
                  globalThis.location.href = toNavHref(menu_0, locale);
                }
              }} onKeyDown={e_3 => handleKeyDown(e_3, index_1, menu_0)} onFocus={() => handleFocus(index_1)}>
                    <Text variant="body-1" as="span" className={styles.navLinkText}>
                      {menu_0.title}
                    </Text>
                    {menu_0.items?.length && <span className={styles.chevronIcon}>
                        <NavArrowRight decorative size="large" />
                      </span>}
                  </a>
                  {menu_0.items?.length && <div role="menu" aria-label={t('aria.submenu', {
                title: menu_0.title
              })} className={clsx(styles.dropdown, {
                [styles.dropdownHidden]: navigationDropdownIndex !== index_1 && navigationOpen,
                [styles.partner]: partner !== undefined,
                [styles.promotion]: isPromoLive
              })}
              // data-type is title-derived; kept only for E2E selectors, style off data-layout/data-wide
              data-type={menu_0.title?.toLowerCase()} data-layout={layoutOf(menu_0)} data-wide={isWide(menu_0)} data-dropdown-index={index_1} data-focused={focusedMenuIndex === index_1} onTouchStart={e_4 => e_4.stopPropagation()} onTouchMove={e_5 => e_5.stopPropagation()} onClick={e_6 => e_6.stopPropagation()} style={{
                WebkitOverflowScrolling: 'touch'
              }}>
                      <div className={styles.dropdownHeader}>
                        <button type="button" className={styles.backButton} onClick={() => setNavigationDropdownIndex(null)}>
                          <NavArrowLeft decorative size="large" />
                          {menu_0.title}
                        </button>
                      </div>
                      <div className={styles.dropdownContent} data-wide={isWide(menu_0)} data-layout={layoutOf(menu_0)} data-tiles-only={isTilesOnly(menu_0)} data-type={menu_0.title?.toLowerCase()} style={{
                  WebkitOverflowScrolling: 'touch'
                }}>
                        {isListDetail(menu_0) ? <NavDropdownListDetail groups={visibleGroups(menu_0)} surveyHandler={surveyHandler} navigationOpen={navigationOpen} openDropdown={() => setNavigationDropdownIndex(index_1)} /> : <NavDropdownItems items={flatItems(menu_0)} surveyHandler={surveyHandler} />}
                        {menu_0.tiles?.length ? <NavDropdownTiles tiles={menu_0.tiles} surveyHandler={surveyHandler} /> : null}
                      </div>
                    </div>}
                </li>)}
            </ul>
          </nav>
        </div>

        {/* Desktop Actions */}
        <div className={styles.desktopActions}>
          {showContactOptions && <DialogTrigger isOpen={isDesktopContactPopoverOpen} onOpenChange={setIsDesktopContactPopoverOpen}>
              <Button aria-label={t('aria.call')} className={styles.contactButton} onClick={() => {
            trackEvent('navigation_contact_btn', {
              event_category: 'Contact',
              event_label: 'navigation_call_us_button'
            });
          }}>
                <Call decorative size="large" />
              </Button>
              <Popover placement="bottom" maxWidth="300px" close ariaLabel={t('aria.contact_phone')} onOpenChange={setIsDesktopContactPopoverOpen} border={false}>
                <Stack space="--by-space-200">
                  <div className={styles.contactPopoverContent}>
                    <div className={styles.contactPopoverIcon}>
                      <CallSolid size="scale" decorative />
                    </div>
                    <div className={styles.contactPopoverText}>
                      <p className={styles.contactPopoverTitle}>{t('common.phone')}</p>
                      <div className={styles.contactPopoverDescription}>
                        <p>{t('common.phone_1_display')}</p>
                        {t('common.phone_2_display') && <p>{t('common.phone_2_display')}</p>}
                      </div>
                    </div>
                  </div>
                </Stack>
              </Popover>
            </DialogTrigger>}
          <BuildTripButton />
          <PreferencesDialog />
          <NavSearch />
        </div>
      </div>
    </header>;
}
export default Navigation;
function _temp() {
  trackEvent("navigation_contact_btn", {
    event_category: "Contact",
    event_label: "navigation_contact_btn"
  });
}